import React, { useState, useEffect } from "react";
import "./DashboardInvestigador.css";
import { useNavigate } from "react-router-dom";

// Componentes Modulares (Vistas)
import ExecutiveSummary from "./components/ExecutiveSummary";
import MisPacientes from "./components/MisPacientes";
import MisCasos from "./components/MisCasos";
import GestionProyectos from "./components/GestionProyectos";
import RegistrarPaciente from "./components/RegistrarPaciente";
import BuscarPaciente from "./components/BuscarPaciente";
import RegistrarCaso from "./components/RegistrarCaso";
import AnonymizedReportView from "./components/AnonymizedReportView";
import ProfileManagement from "./components/ProfileManagement";

// Servicios y Utilidades
import { Apiurl } from "../../shared/services/apirest";
import axios from "axios";
import { getAuthHeader } from "./utils/investigadorUtils";

const DashboardInvestigador = () => {
    const navigate = useNavigate();
    const [view, setView] = useState("home");
    const [selectedPaciente, setSelectedPaciente] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    const [showUserMenu, setShowUserMenu] = useState(false);

    // Contexto del Proyecto Activo
    const [activeProjectId, setActiveProjectId] = useState(localStorage.getItem("activeProjectId") || null);
    const [projects, setProjects] = useState([]);

    const researcherName = (localStorage.getItem("nombre_investigador") || "Investigador").trim();

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const res = await axios.get(`${Apiurl}casos/proyectos/`, getAuthHeader());
                setProjects(res.data);
                if (!activeProjectId && res.data.length > 0) {
                    setActiveProjectId(res.data[0].id);
                    localStorage.setItem("activeProjectId", res.data[0].id);
                }
            } catch (err) {
                console.error("Error loading projects context:", err);
            }
        };
        fetchProjects();
    }, [activeProjectId]);

    const handleLogout = () => {
        localStorage.clear();
        navigate("/");
    };

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3500);
    };

    const renderContent = () => {
        switch (view) {
            case "home":
                return <ExecutiveSummary setView={setView} activeProjectId={activeProjectId} />;
            case "misPacientes":
                return (
                    <MisPacientes
                        activeProjectId={activeProjectId}
                        showToast={showToast}
                        onEdit={(p) => { setEditingItem(p); setView("editarPaciente"); }}
                        onViewHistory={(p) => { setSelectedPaciente(p); setView("historialPaciente"); }}
                    />
                );
            case "registrarPaciente":
                return (
                    <RegistrarPaciente
                        showToast={showToast}
                        setView={setView}
                        setSelectedPaciente={setSelectedPaciente}
                        activeProjectId={activeProjectId}
                        projects={projects}
                    />
                );
            case "editarPaciente":
                return (
                    <RegistrarPaciente
                        showToast={showToast}
                        setView={setView}
                        initialData={editingItem}
                        isEditMode={true}
                        onCancel={() => { setEditingItem(null); setView("misPacientes"); }}
                        activeProjectId={activeProjectId}
                        projects={projects}
                    />
                );
            case "buscarPaciente":
                return (
                    <BuscarPaciente
                        setView={setView}
                        setSelectedPaciente={setSelectedPaciente}
                        showToast={showToast}
                        activeProjectId={activeProjectId}
                    />
                );
            case "registrarCaso":
                return (
                    <RegistrarCaso
                        selectedPaciente={selectedPaciente}
                        setView={setView}
                        showToast={showToast}
                        setSelectedPaciente={setSelectedPaciente}
                        activeProjectId={activeProjectId}
                    />
                );
            case "misCasos":
                return (
                    <MisCasos
                        showToast={showToast}
                        activeProjectId={activeProjectId}
                        onEdit={(c) => { setEditingItem(c); setView("editarCaso"); }}
                    />
                );
            case "historialPaciente":
                return (
                    <MisCasos
                        showToast={showToast}
                        activeProjectId={activeProjectId}
                        initialSearch={selectedPaciente?.documento}
                        onEdit={(c) => { setEditingItem(c); setView("editarCaso"); }}
                    />
                );
            case "editarCaso":
                return (
                    <RegistrarCaso
                        setView={setView}
                        showToast={showToast}
                        initialData={editingItem}
                        isEditMode={true}
                        onCancel={() => { setEditingItem(null); setView("misCasos"); }}
                        activeProjectId={activeProjectId}
                    />
                );
            case "misProyectos":
                return (
                    <GestionProyectos
                        showToast={showToast}
                        activeProjectId={activeProjectId}
                        setActiveProjectId={setActiveProjectId}
                    />
                );
            case "reporteAnonimizado":
                return <AnonymizedReportView />;
            case "perfil":
                return <ProfileManagement showToast={showToast} />;
            default:
                return (
                    <div className="text-center py-5">
                        <i className="bi bi-cone-striped fs-1 text-warning mb-3"></i>
                        <h4>Módulo en Construcción</h4>
                        <p className="text-muted">La vista <b>{view}</b> estará disponible pronto.</p>
                        <button className="btn btn-success" onClick={() => setView("home")}>Volver al Inicio</button>
                    </div>
                );
        }
    };

    return (
        <div className="dashboard-investigador-container">
            {/* Navegación Lateral (Sidebar) */}
            <aside className="investigador-sidebar">
                <div className="sidebar-header">
                    <div className="investigador-badge">
                        <i className="bi bi-shield-check me-2"></i>
                        Panel de Investigación
                    </div>
                </div>

                <nav className="sidebar-nav">
                    <div className="nav-section">Análisis Principal</div>
                    <button className={`nav-link-custom ${view === 'home' ? 'active' : ''}`} onClick={() => setView("home")}>
                        <i className="bi bi-pie-chart-fill"></i> Resumen Epidemiológico
                    </button>
                    <button className={`nav-link-custom ${view === 'misProyectos' ? 'active' : ''}`} onClick={() => setView("misProyectos")}>
                        <i className="bi bi-journal-medical"></i> Proyectos de Investigación
                    </button>

                    <div className="nav-section">Gestión de Campo</div>
                    <button className={`nav-link-custom ${view === 'registrarPaciente' ? 'active' : ''}`} onClick={() => setView("registrarPaciente")}>
                        <i className="bi bi-person-plus-fill"></i> Registro de Paciente
                    </button>
                    <button className={`nav-link-custom ${view === 'misPacientes' || view === 'editarPaciente' ? 'active' : ''}`} onClick={() => setView("misPacientes")}>
                        <i className="bi bi-database-fill"></i> Base de Datos Clínicos
                    </button>
                    <button className={`nav-link-custom ${['buscarPaciente', 'registrarCaso', 'misCasos', 'editarCaso', 'historialPaciente'].includes(view) ? 'active' : ''}`}
                        onClick={() => setView("misCasos")}>
                        <i className="bi bi-activity"></i> Análisis Epidemiológico
                    </button>

                    <div className="nav-section">Datos y Reportes</div>
                    <button className={`nav-link-custom ${view === 'reporteAnonimizado' ? 'active' : ''}`} onClick={() => setView("reporteAnonimizado")}>
                        <i className="bi bi-file-earmark-bar-graph-fill"></i> Reportes Anonimizados
                    </button>
                </nav>

                <div className="sidebar-footer">
                    <div className="active-project-info p-2 mt-auto">
                        <div className="small text-muted mb-1 text-uppercase fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>Proyecto Activo:</div>
                        <div className="project-code-badge">
                            <i className="bi bi-bookmark-star-fill me-1"></i>
                            {projects.find(p => p.id === activeProjectId)?.codigo || "SIN PROYECTO"}
                        </div>
                    </div>
                </div>
            </aside>

            {/* Área de Contenido Principal */}
            <div className="investigador-content">
                <header className="content-header p-3 d-flex justify-content-between align-items-center">
                    <div>
                        <h1 className="main-title">Panel de Investigación Epidemiológica</h1>
                        <p className="main-subtitle m-0">Indicadores del proyecto activo y actividad de campo registrada en la plataforma</p>
                    </div>

                    <div className="user-control-group">
                        <div className="dropdown">
                            <button
                                className="user-menu-btn"
                                onClick={() => setShowUserMenu(!showUserMenu)}
                            >
                                <div className="avatar-circle">{researcherName.charAt(0)}</div>
                                <span className="d-none d-md-block fw-bold small text-dark">{researcherName}</span>
                                <i className={`bi bi-chevron-${showUserMenu ? 'up' : 'down'} small text-muted`}></i>
                            </button>

                            {showUserMenu && (
                                <div className="dropdown-menu show shadow-sm border-0 position-absolute end-0 mt-2" style={{ borderRadius: '12px', minWidth: '180px' }}>
                                    <button className="dropdown-item py-2 px-3 small border-bottom" onClick={() => { setView("perfil"); setShowUserMenu(false); }}>
                                        <i className="bi bi-person me-2 text-primary"></i> Mi Perfil
                                    </button>
                                    <button className="dropdown-item py-2 px-3 small text-danger fw-bold" onClick={handleLogout}>
                                        <i className="bi bi-box-arrow-right me-2"></i> Cerrar Sesión
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main className="content-body animate__animated animate__fadeIn">
                    {renderContent()}
                </main>
            </div>

            {/* Sistema de Notificaciones (Toasts) */}
            {toast.show && (
                <div className={`toast-container position-fixed bottom-0 end-0 p-3`} style={{ zIndex: 1100 }}>
                    <div className={`toast show align-items-center text-white bg-${toast.type} border-0 shadow-lg`} role="alert" style={{ borderRadius: '12px' }}>
                        <div className="d-flex p-2">
                            <div className="toast-body fw-bold">
                                <i className={`bi bi-${toast.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
                                {toast.message}
                            </div>
                            <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToast({ ...toast, show: false })}></button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardInvestigador;

